import React, { useState, useEffect } from "react";

import {
  Box,
  TextField,
  Typography,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  FormControlLabel,
  Button,
  Stack,
  RadioGroup,
  Radio,
  FormControl,
  InputLabel,
  Alert,
} from "@mui/material";

import Snackbar from "@mui/material/Snackbar";
import { useClientInfo, axiosInstance } from "../../utils/commonImports";
import InputAdornment from "@mui/material/InputAdornment";
import MyDatePicker from "../../components/MyDatePicker";
import CustomerDropdown from "../../components/CustomerDropdown";
import { createDocument } from "../../api/numberingApi";
import { useParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";

import PreviewModal from "../../components/preview/PreviewModal";

import { numberToIndianWords } from "../../utils/numberToIndianWords";
import { normalizeDateAny } from "../../utils/dateUtils";

// ================= GST Calculation Function =================
function calculateGstAmounts({
  tax_preference,
  place_of_supply,
  client_place_of_supply,
  taxable_amount,
  cgst_percent = 0,
  sgst_percent = 0,
  igst_percent = 0,
}) {
  let cgst = 0;
  let sgst = 0;
  let igst = 0;

  if (tax_preference === "Taxable") {
    if (place_of_supply === client_place_of_supply) {
      cgst = (taxable_amount * cgst_percent) / 100;
      sgst = (taxable_amount * sgst_percent) / 100;
    } else {
      igst = (taxable_amount * igst_percent) / 100;
    }
  }

  return { cgst, sgst, igst };
}

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Single source of truth for batta of one duty row
const getRowBatta = (r = {}) => {
  // 1) direct field, if the API provides a real value
  const direct = toNum(r.trip_charges_batta);
  if (direct > 0) return direct;

  // 2) already-mapped rows
  const mapped = toNum(r.batta_main);
  if (mapped > 0) return mapped;

  // 3) build from components (same logic as mapTripDuty)
  const duty = (r.duty || "").trim();
  if (duty === "Local") {
    return toNum(r.trip_charges_local_batta);
  }
  return (
    toNum(r.trip_charges_os_batta_total_amount) +
    toNum(r.trip_charges_os_night_batta_total_amount)
  );
};

const getTodayDateString = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// export default function InvoiceFormLocal({ invoiceId }) {
export default function InvoiceFormLocal({
  invoiceId,
  editData,
  onCancel,
  onSuccess,
}) {
  const { clientId } = useClientInfo();
  const [clientDetails, setClientDetails] = useState({});
  const [filterType, setFilterType] = useState("all");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [customers, setCustomers] = useState([]);

  // const [tripDuties, setTripDuties] = useState([]);
  // const [selectedIndices, setSelectedIndices] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [invoiceSettings, setInvoiceSettings] = useState(null);

  const [rows, setRows] = useState([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState([]);
  const selectedRows = rows.filter((_, idx) => selectedIndex.includes(idx));

  const [form, setForm] = useState({
    number: "",
    date: getTodayDateString(),
    reference: "",
    po_no: "",
    customerid: 0,
    customers: "",
    gst_treatment: "",
    tax_preference: "",
    customergstin: "",
    place_of_supply: "",
    cal_gst_parking_toll_permit: "",
    cal_gst_batta: "",
    cal_gst_others: "",
    payment_mode: "",
    client_place_of_supply: "",
    taxable_amount: 0,
    cgst_amount: 0,
    sgst_amount: 0,
    igst_amount: 0,
    gross_before_tax: 0,
    advance_amount: 0,
    voucher_advance_amount: 0,
    coin_adjust: 0,
    addition_amount: 0,
    discount_amount: 0,
    deduction_amount: 0,
    hsn_code: "996601",
    gst_notification: "",
    remarks: "",

    // any other fields you use...
  });

  const { invoiceParam } = useParams(); // URL param like /invoice/:invoiceParam
  const existingDutyIds =
    editData?.trip_duties?.map((duty) => duty.trip_duty_id) || [];

  const [invoiceNumber, setInvoiceNumber] = useState(form.number);

  const [basic_amount, setBasic_amount] = useState(0);
  const [fuel_esc_amount, setFuel_esc_amount] = useState(0);
  const [escort_amount, setEscort_amount] = useState(0);
  const [batta_amount, setBatta_amount] = useState(0);
  const [parking_amount, setParking_amount] = useState(0);
  const [toll_amount, setToll_amount] = useState(0);
  const [permit_amount, setPermit_amount] = useState(0);
  const [others_amount, setOthers_amount] = useState(0);
  const [clientBanks, setClientBanks] = useState([]);

  const [duties, setDuties] = useState([]);
  const [billedDuties, setBilledDuties] = useState([]); // duties already billed
  const [unbilledDuties, setUnbilledDuties] = useState([]); // pending duties
  const [selectedDutyIds, setSelectedDutyIds] = useState([]); // checked ids in popup
  const [showDutySelector, setShowDutySelector] = useState(false); // popup

  // Voucher states
  const [vouchers, setVouchers] = useState([]);
  const [selectedVoucherIds, setSelectedVoucherIds] = useState([]);
  const [voucherSelectAll, setVoucherSelectAll] = useState(false);
  const [showVoucherTable, setShowVoucherTable] = useState(false);

  const currentInvoiceId = invoiceId || form?.invoiceid || editData?.invoiceid;

  // Determine edit mode based on currentInvoiceId
  const isEditMode = Boolean(currentInvoiceId);

  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewHTML, setPreviewHTML] = useState("");

  // const [showPrintConfirm, setShowPrintConfirm] = useState(false);
  // let tempInvoiceIdRef = useRef(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (!clientId) return;

    async function fetchInvoiceSettings() {
      try {
        const res = await axiosInstance.get(
          `/api/clients/${clientId}/invoice-settings`,
        );

        setInvoiceSettings(res.data?.invoice || null);
      } catch (error) {
        console.error("❌ Failed to fetch invoice settings:", error);
        setInvoiceSettings(null);
      }
    }

    fetchInvoiceSettings();
  }, [clientId]);

  useEffect(() => {
    const selectedRows = rows.filter((_, idx) => selectedIndex.includes(idx));

    setBasic_amount(
      selectedRows.reduce(
        (acc, r) => acc + Number(r.trip_charges_basic_amount || 0),
        0,
      ),
    );
    setFuel_esc_amount(
      selectedRows.reduce(
        (acc, r) => acc + Number(r.trip_charges_fuel_esculation_amount || 0),
        0,
      ),
    );
    setEscort_amount(
      selectedRows.reduce(
        (acc, r) => acc + Number(r.trip_charges_escort_amount || 0),
        0,
      ),
    );
    setBatta_amount(selectedRows.reduce((acc, r) => acc + getRowBatta(r), 0));

    setParking_amount(
      selectedRows.reduce(
        (acc, r) => acc + Number(r.trip_charges_parking || 0),
        0,
      ),
    );
    setToll_amount(
      selectedRows.reduce(
        (acc, r) => acc + Number(r.trip_charges_toll_fasttag || 0),
        0,
      ),
    );
    setPermit_amount(
      selectedRows.reduce(
        (acc, r) => acc + Number(r.trip_charges_permit || 0),
        0,
      ),
    );
    setOthers_amount(
      selectedRows.reduce(
        (acc, r) => acc + Number(r.trip_charges_others || 0),
        0,
      ),
    );
  }, [selectedIndex, rows]);

  // You should manage these states with hooks and input change handlers in real form
  const addition_amount = 0;
  const discount_amount = 0;
  const deduction_amount = 0;
  const hsn_code = "996601";
  const gst_notification = "";
  const remarks = "";

  // Assuming you have customerData loaded e.g. from API or state
  const customerData = {
    cal_gst_batta: "Yes", // or "No"
    cal_gst_others: "No",
    cal_gst_parking_toll_permit: "Yes",
  };

  // Calculate Gross amount before tax (you may want to build this in logic)
  const gross_before_tax =
    basic_amount +
    fuel_esc_amount +
    escort_amount +
    batta_amount +
    parking_amount +
    toll_amount +
    permit_amount +
    others_amount +
    addition_amount -
    deduction_amount -
    discount_amount;

  const taxable_amount =
    basic_amount +
    fuel_esc_amount +
    escort_amount +
    (form.cal_gst_batta === "Yes" ? batta_amount : 0) +
    (form.cal_gst_parking_toll_permit === "Yes"
      ? parking_amount + toll_amount + permit_amount
      : 0) +
    (form.cal_gst_others === "Yes" ? others_amount : 0) +
    addition_amount -
    discount_amount -
    deduction_amount;

  const paymentModes = ["Postpaid", "COD"];

  useEffect(() => {
    if (!clientId) return;

    async function fetchClientData() {
      try {
        const res = await axiosInstance.get(`/api/clients/${clientId}`);

        const client = res.data.client;

        setClientDetails(client);

        setForm((prev) => ({
          ...prev,
          client_place_of_supply: client.place_of_supply || "",
        }));
      } catch (error) {
        console.error("❌ Failed to fetch client details:", error);
      }
    }

    fetchClientData();
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    async function fetchCustomers() {
      try {
        const response = await axiosInstance.get("/api/customers", {
          params: { client_id: clientId },
        });
        setCustomers(response.data || []);
      } catch (error) {
        console.error("Error fetching customers:", error);
        setCustomers([]);
      }
    }
    fetchCustomers();
  }, [clientId]);

  const [igstPercent, setIgstPercent] = useState(0);
  const [cgstPercent, setCgstPercent] = useState(0);
  const [sgstPercent, setSgstPercent] = useState(0);

  useEffect(() => {
    if (!clientId) return;

    async function fetchGSTSettings() {
      try {
        const res = await axiosInstance.get(
          `/api/clients/${clientId}/gst-settings`,
        );

        const gst = res.data?.gst || {};

        // Sync GST percent values
        setCgstPercent(gst.tax_rate_cgst || 0);
        setSgstPercent(gst.tax_rate_sgst || 0);
        setIgstPercent(gst.tax_rate_igst || 0);

        // Sync into form for NEW invoice
        if (!isEditMode) {
          setForm((prev) => ({
            ...prev,
            cgst_percent: gst.tax_rate_cgst || 0,
            sgst_percent: gst.tax_rate_sgst || 0,
            igst_percent: gst.tax_rate_igst || 0,
            hsn_code: gst.hsn_code || "",
            gst_notification: gst.gst_notification || "",
          }));
        }
      } catch (error) {
        console.error("Error fetching GST settings:", error);
      }
    }

    fetchGSTSettings();
  }, [clientId]);

  useEffect(() => {
    const { cgst, sgst, igst } = calculateGstAmounts({
      tax_preference: form.tax_preference,
      place_of_supply: form.place_of_supply,
      client_place_of_supply: form.client_place_of_supply,
      taxable_amount: Number(taxable_amount) || 0,
      cgst_percent: Number(form.cgst_percent) || 0,
      sgst_percent: Number(form.sgst_percent) || 0,
      igst_percent: Number(form.igst_percent) || 0,
    });

    setForm((prev) => ({
      ...prev,
      cgst_amount: cgst,
      sgst_amount: sgst,
      igst_amount: igst,
      taxable_amount,
    }));
  }, [
    taxable_amount,
    form.tax_preference,
    form.place_of_supply,
    form.client_place_of_supply,
    form.cgst_percent,
    form.sgst_percent,
    form.igst_percent,
  ]);

  // 1. Keep discount_amount synced with discount_percent & basic_amount
  useEffect(() => {
    const discountPercent = Number(form.discount_percent) || 0;
    const discountAmount = (basic_amount * discountPercent) / 100;
    if (discountAmount !== form.discount_amount) {
      setForm((prev) => ({ ...prev, discount_amount: discountAmount }));
    }
  }, [form.discount_percent, basic_amount]);

  // 2. Recalculate gross_before_tax and taxable_amount anytime amounts change
  useEffect(() => {
    const gross_before_tax =
      Number(basic_amount) +
      Number(fuel_esc_amount) +
      Number(escort_amount) +
      Number(batta_amount) +
      Number(parking_amount) +
      Number(toll_amount) +
      Number(permit_amount) +
      Number(others_amount) +
      Number(form.addition_amount || 0) -
      Number(form.deduction_amount || 0) -
      Number(form.discount_amount || 0);

    const taxable_amount =
      Number(basic_amount) +
      Number(fuel_esc_amount) +
      Number(escort_amount) +
      (form.cal_gst_batta === "Yes" ? Number(batta_amount) : 0) +
      (form.cal_gst_parking_toll_permit === "Yes"
        ? Number(parking_amount) + Number(toll_amount) + Number(permit_amount)
        : 0) +
      (form.cal_gst_others === "Yes" ? Number(others_amount) : 0) +
      Number(form.addition_amount || 0) -
      Number(form.deduction_amount || 0) -
      Number(form.discount_amount || 0);

    setForm((prev) => ({
      ...prev,
      gross_before_tax,
      taxable_amount,
    }));
  }, [
    basic_amount,
    fuel_esc_amount,
    escort_amount,
    batta_amount,
    parking_amount,
    toll_amount,
    permit_amount,
    others_amount,
    form.cal_gst_batta,
    form.cal_gst_parking_toll_permit,
    form.cal_gst_others,
    form.addition_amount,
    form.deduction_amount,
    form.discount_amount,
  ]);

  function roundCoinAdjust(value) {
    const integerPart = Math.floor(value);
    const fraction = value - integerPart;

    if (fraction < 0.5) {
      return integerPart; // round down to integer
    } else {
      return integerPart + 1; // round up to next integer
    }
  }

  useEffect(() => {
    const baseAmount =
      Number(form.gross_before_tax) +
      Number(form.cgst_amount) +
      Number(form.sgst_amount) +
      Number(form.igst_amount) -
      (Number(form.advance_amount) || 0) -
      (Number(form.voucher_advance_amount) || 0);

    if (baseAmount <= 0) return; // skip calculation

    // Calculate raw difference to round
    const difference = baseAmount - Math.floor(baseAmount);

    // Decide coin_adjust based on difference rounded by custom function
    const roundedAdjust = roundCoinAdjust(difference);

    // Calculate coin_adjust needed to bring baseAmount to an integer rounded as requested
    const newCoinAdjust = roundedAdjust - difference;

    setForm((prev) => ({ ...prev, coin_adjust: newCoinAdjust }));
  }, [
    form.gross_before_tax,
    form.cgst_amount,
    form.sgst_amount,
    form.igst_amount,
    form.advance_amount,
    form.voucher_advance_amount,
  ]);

  // Add to your state:
  const [netAmount, setNetAmount] = useState(0);

  useEffect(() => {
    let baseAmount = Number(form.gross_before_tax) || 0;

    const isRCM = form.gst_treatment === "Registered Business-Under RCM";

    // REGULAR GST → ADD GST AMOUNT
    if (!isRCM) {
      baseAmount +=
        Number(form.cgst_amount) +
        Number(form.sgst_amount) +
        Number(form.igst_amount);
    }

    // RCM → DO NOT ADD GST (GST only shown inside invoice)
    // baseAmount stays same

    // SUBTRACT ADVANCES
    baseAmount -=
      Number(form.advance_amount || 0) +
      Number(form.voucher_advance_amount || 0);

    // ADD COIN ADJUST
    baseAmount += Number(form.coin_adjust || 0);

    setNetAmount(baseAmount);
  }, [
    form.gross_before_tax,
    form.cgst_amount,
    form.sgst_amount,
    form.igst_amount,
    form.advance_amount,
    form.voucher_advance_amount,
    form.coin_adjust,
    form.gst_treatment,
  ]);

  // Effect to keep selectedDutyIds in sync with selectedIndex/selectedRows
  useEffect(() => {
    const ids = selectedIndex.map((i) => rows[i]?.trip_duty_id).filter(Boolean);
    setSelectedDutyIds(ids);
  }, [selectedIndex, rows]);

  useEffect(() => {
    const finalCustomerId =
      form.invoiceid && form.invoiceid !== "0"
        ? form.customerid
        : selectedCustomer;

    if (!clientId || !finalCustomerId) return;

    const fetchTripDuties = async () => {
      try {
        setLoadingTrips(true);

        const invoiceParam =
          form.invoiceid && form.invoiceid !== "0" ? form.invoiceid : "new";

        const res = await axiosInstance.get(
          `/api/invoices/${invoiceParam}/tripduties`,
          { params: { client_id: clientId, customer_id: finalCustomerId } },
        );

        const { selected = [], pending = [] } = res.data;

        // debug only, safe position (after the destructure)
        const sample = selected[0] || pending[0];
        if (sample) {
          console.log(
            "BATTA KEYS:",
            Object.entries(sample).filter(([k]) => /batta/i.test(k)),
          );
        }

        if (invoiceParam !== "new") {
          setRows(selected);
          setSelectedIndex(selected.map((_, index) => index));
        } else {
          const normalizedPending = pending.map((d) => ({
            ...d,
            duty_id: d.trip_duty_id || d.id,
          }));
          setRows(normalizedPending);
          setSelectedIndex([]);
        }
      } catch (error) {
        console.error("Error fetching trip duties:", error);
        setRows([]);
        setSelectedIndex([]);
      } finally {
        setLoadingTrips(false);
      }
    };

    fetchTripDuties();
  }, [selectedCustomer, form.customerid, form.invoiceid, clientId]);

  useEffect(() => {
    async function loadBanks() {
      try {
        const response = await axiosInstance.get(
          `/api/client-banks/${clientId}`,
        );

        setClientBanks(response.data || []);
      } catch (err) {
        console.error("Failed to load client banks", err);
      }
    }

    loadBanks();
  }, [clientId]);

  useEffect(() => {
    if (!form.customerid || !clientId) return;

    async function loadLedgerId() {
      try {
        const res = await axiosInstance.get("/api/customers/ledger-id", {
          params: { client_id: clientId, customer_id: form.customerid },
        });

        setForm((prev) => ({
          ...prev,
          customer_ledger_id: res.data.ledger_id,
        }));
      } catch (err) {
        console.error("❌ Ledger Load Error:", err);
      }
    }

    loadLedgerId();
  }, [form.customerid, clientId]);

  // inside your component
  useEffect(() => {
    if (showVoucherTable) return;
    if (!form.customer_ledger_id || !clientId) return;

    async function loadVouchersForEdit() {
      try {
        // NEW invoice → only pending vouchers (same as before)
        if (!form.invoiceid) {
          const pendingRes = await axiosInstance.get("/api/vouchers/pending", {
            params: {
              client_id: clientId,
              ledger_id: form.customer_ledger_id,
            },
          });

          const pending = pendingRes.data || [];
          // Normalize shape: ensure voucher_id present
          const normalizedPending = pending.map((p) => ({
            ...p,
            voucher_id: p.voucher_id || p.id,
          }));

          setVouchers(normalizedPending);
          setSelectedVoucherIds([]);
          setShowVoucherTable(Boolean(normalizedPending.length));
          return;
        }

        // EDIT invoice → load only selected initially
        const selectedRes = await axiosInstance.get("/api/vouchers/selected", {
          params: {
            client_id: clientId,
            invoice_id: form.invoiceid,
            customer_id: form.customer_ledger_id,
          },
        });

        const selected = selectedRes.data || [];
        const normalizedSelected = selected.map((s) => ({
          ...s,
          voucher_id: s.voucher_id || s.id,
        }));

        setVouchers(normalizedSelected);
        // Selected ids MUST be voucher_id
        setSelectedVoucherIds(normalizedSelected.map((v) => v.voucher_id));
        setShowVoucherTable(normalizedSelected.length > 0);
      } catch (err) {
        console.error("❌ Voucher Load Error:", err);
      }
    }

    loadVouchersForEdit();
  }, [form.customer_ledger_id, form.invoiceid, clientId]);

  // -------------------------------
  // Select / Unselect Single Voucher
  // -------------------------------
  const handleVoucherSelect = (voucherId) => {
    const id = Number(voucherId);

    setSelectedVoucherIds((prev) => {
      const updated = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id];

      // ⭐ Update total immediately when selection changes
      updateVoucherTotal(updated);

      return updated;
    });
  };

  // -------------------------------
  // Select / Unselect ALL Vouchers
  // -------------------------------
  const handleVoucherSelectAll = () => {
    if (voucherSelectAll) {
      setVoucherSelectAll(false);
      setSelectedVoucherIds([]);
      updateVoucherTotal([]);
    } else {
      const allIds = vouchers
        .map((v) => parseInt(v.voucher_id || v.id, 10)) // ⭐ ensure integer
        .filter((n) => !isNaN(n));

      setVoucherSelectAll(true);
      setSelectedVoucherIds(allIds);
      updateVoucherTotal(allIds);
    }
  };

  const handleOpenVoucherTable = async () => {
    try {
      console.log("🟦 +Voucher clicked → loading pending + selected vouchers");

      if (!clientId || !form.customer_ledger_id) {
        console.log("❌ Missing clientId or customer_ledger_id");
        return;
      }

      // 1) Pending
      const pendingRes = await axiosInstance.get("/api/vouchers/pending", {
        params: {
          client_id: clientId,
          ledger_id: form.customer_ledger_id,
        },
      });
      const pending = pendingRes.data || [];
      const normalizedPending = pending.map((p) => ({
        ...p,
        voucher_id: p.voucher_id || p.id,
      }));

      // 2) Selected (if edit)
      let selected = [];
      if (form.invoiceid) {
        const selectedRes = await axiosInstance.get("/api/vouchers/selected", {
          params: {
            client_id: clientId,
            invoice_id: form.invoiceid,
            customer_id: form.customer_ledger_id,
          },
        });
        selected = selectedRes.data || [];
      }
      const normalizedSelected = selected.map((s) => ({
        ...s,
        voucher_id: s.voucher_id || s.id,
      }));

      // 3) Merge: keep selected first, then pending (avoid duplicates by voucher_id)
      const selectedMap = new Map(
        normalizedSelected.map((s) => [s.voucher_id, s]),
      );
      normalizedPending.forEach((p) => {
        if (!selectedMap.has(p.voucher_id)) selectedMap.set(p.voucher_id, p);
      });

      const merged = Array.from(selectedMap.values());

      console.log("🟢 Final merged voucher list:", merged);

      setVouchers(merged);

      // Keep previously selected vouchers that are still present; otherwise only selected from API
      const currentlySelected =
        normalizedSelected.length > 0
          ? normalizedSelected.map((v) => v.voucher_id)
          : [];
      setSelectedVoucherIds(currentlySelected);

      setShowVoucherTable(true);
    } catch (err) {
      console.error("❌ Error loading voucher table:", err);
    }
  };

  // -------------------------------
  // Recalculate Total Advance
  // -------------------------------
  const updateVoucherTotal = (ids) => {
    const total = vouchers
      .filter((v) => ids.includes(v.voucher_id || v.id))
      .reduce((sum, v) => sum + Number(v.credit_amount || 0), 0);

    setForm((prev) => ({ ...prev, voucher_advance_amount: total }));
  };

  useEffect(() => {
    if (previewHTML) {
      setShowPreview(true);
    }
  }, [previewHTML]);

  const handleFilterChange = (e) => setFilterType(e.target.value);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectAll = (e) => {
    setSelectedAll(e.target.checked);
    setSelectedIndex(e.target.checked ? rows.map((_, idx) => idx) : []);
  };

  const handleSelectRow = (idx) => {
    if (selectedIndex.includes(idx)) {
      setSelectedIndex(selectedIndex.filter((i) => i !== idx));
    } else {
      setSelectedIndex([...selectedIndex, idx]);
    }
  };

  // --------------------
  // +DUTY POPUP HANDLERS
  // --------------------

  const handleAddDutyClick = async () => {
    try {
      console.log("🟦 +Duty clicked → loading pending + selected duties");

      const invoiceParam =
        form.invoiceid && form.invoiceid !== "0" ? form.invoiceid : "new";

      const res = await axiosInstance.get(
        `/api/invoices/${invoiceParam}/tripduties`,
        {
          params: {
            client_id: clientId,
            customer_id: form.customerid,
          },
        },
      );

      const billed = res.data.selected || [];
      const pending = res.data.pending || [];

      console.log("RAW Pending Response:", res.data.pending);
      console.log("📘 Billed:", billed);
      console.log("📙 Pending:", pending);

      // -----------------------------------------------------
      // NORMALIZE IDs + OPEN_DATE BEFORE USING IN TABLE
      // -----------------------------------------------------
      const normalizedBilled = billed.map((d) => ({
        ...d,
        duty_id: d.trip_duty_id || d.id,
        open_date: normalizeDateAny(d.open_date),
      }));

      const normalizedPending = pending.map((d) => ({
        ...d,
        duty_id: d.trip_duty_id || d.id,
        open_date: normalizeDateAny(d.open_date),
      }));

      // update originals
      setBilledDuties(normalizedBilled);
      setUnbilledDuties(normalizedPending);

      setSelectedDutyIds(normalizedBilled.map((d) => d.duty_id));

      // -----------------------------------------------------
      // MERGE & MAP TO FINAL TABLE ROWS
      // -----------------------------------------------------
      const merged = [...normalizedBilled, ...normalizedPending];

      // const mappedRows = merged.map((duty, index) =>
      //   mapTripDuty({
      //     ...duty,
      //     serial: index + 1,
      //     // open_date: normalizeDateAny(duty.open_date), // 🔥 final safeguard
      //   })
      // );

      console.log("🟢 FINAL TABLE ROWS:", merged);

      setRows(merged);

      setShowDutySelector(true);
    } catch (err) {
      console.log("❌ Error loading duties:", err);
    }
  };

  ///-------------- Saving Invoice Data to invoices Db -----------------
  useEffect(() => {
    if (editData) {
      setForm({
        invoiceid: editData.invoiceid || editData.invoiceId || "", // Added invoiceid here
        client_id: editData.client_id || "",
        fyid: editData.fyid || "",
        bill_type: editData.bill_type || "",
        number: editData.invoice_number || "",
        date: editData.invoice_date ? new Date(editData.invoice_date) : null,
        customerid: editData.customer_id || "",
        reference: editData.reference || "",
        po_no: editData.po_no || "",
        payment_mode: editData.payment_mode || "",
        period_from: editData.period_from || null,
        period_to: editData.period_to || null,
        header: editData.header || "",
        basic_amount: Number(editData.basic_amount) || 0,
        escort_amount: Number(editData.escort_amount) || 0,
        batta_amount: Number(editData.batta_amount) || 0,
        parking_amount: Number(editData.parking_amount) || 0,
        others_amount: Number(editData.others_amount) || 0,
        permit_amount: Number(editData.permit_amount) || 0,
        toll_amount: Number(editData.toll_amount) || 0,
        fuel_ex_remarks: editData.fuel_ex_remarks || "",
        fuel_esc_amount: Number(editData.fuel_esc_amount) || 0,
        addition_remarks: editData.addition_remarks || "",
        addition_amount: Number(editData.addition_amount) || 0,
        deduction_remarks: editData.deduction_remarks || "",
        deduction_amount: Number(editData.deduction_amount) || 0,
        discount_percent: Number(editData.discount_percent) || 0,
        discount_amount: Number(editData.discount_amount) || 0,
        place_of_supply: editData.place_of_supply || "",
        tax_preference: editData.tax_preference || "",
        gst_treatment_id: editData.gst_treatment_id || null,
        gst_treatment: editData.gst_treatment || "",
        gst_notification: editData.gst_notification || "",
        hsn_code: editData.hsn_code || "",
        igst_percent: Number(editData.igst_percent) || 0,
        cgst_percent: Number(editData.cgst_percent) || 0,
        sgst_percent: Number(editData.sgst_percent) || 0,
        cal_gst_parking_toll_permit: editData.cal_gst_parking_toll_permit || "",
        cal_gst_batta: editData.cal_gst_batta || "",
        cal_gst_others: editData.cal_gst_others || "",
        igst_amount: Number(editData.igst_amount) || 0,
        cgst_amount: Number(editData.cgst_amount) || 0,
        sgst_amount: Number(editData.sgst_amount) || 0,
        advance_amount: Number(editData.advance_amount) || 0,
        voucher_advance_amount: Number(editData.voucher_advance_amount) || 0,
        coin_adjust: Number(editData.coin_adjust) || 0,
        net_amount: Number(editData.net_amount) || 0,
        word_amount: editData.word_amount || "",
        no_of_tripsheets: Number(editData.no_of_tripsheets) || 0,
        naration: editData.naration || "",
        annexureid: editData.annexureid || null,
        client_place_of_supply: editData.client_place_of_supply || "",
        // Add other fields as needed
      });
    }
  }, [editData]);

  // ------------------------------------------------------
  // MAIN TRIP MAPPING FUNCTION (self-contained)
  // ------------------------------------------------------
  function mapTripDuty(t) {
    function showDash(val) {
      if (val === null || val === undefined) return "-";
      if (val === "") return "-";
      if (isNaN(val)) return "-";
      return Number(val);
    }

    function showNumber(val) {
      return val && !isNaN(val) ? Number(val) : 0;
    }

    function showAmount(val) {
      const num = Number(val);
      return num > 0 ? num : "-";
    }

    function formatSlabDisplay(item) {
      const itemDuty = (item.duty || "").trim();

      if (itemDuty === "Outstation" || itemDuty === "OSDayRate") {
        const days = showNumber(item.trip_charges_days_used);
        const minKm = showNumber(item.trip_charges_slab_km);
        return days > 0 && minKm > 0 ? `${days} / ${days * minKm}` : "-";
      }

      const hours = showNumber(item.trip_charges_slab_hour);
      const kms = showNumber(item.trip_charges_slab_km);
      return hours > 0 || kms > 0 ? `${hours} / ${kms}` : "-";
    }

    // ---------------- BATTA ----------------
    let dutyVal = (t.duty || "").trim();

    // Local batta
    let batta_local =
      dutyVal === "Local" ? showNumber(t.trip_charges_local_batta) : 0;

    // Outstation Day Batta
    let batta_os_day =
      dutyVal !== "Local"
        ? showNumber(t.trip_charges_os_batta_total_amount)
        : 0;

    console.log("Night Batta:", t.trip_charges_os_night_batta_total_amount);

    // Outstation Night Batta  ⭐ NEW
    let batta_os_night =
      dutyVal !== "Local"
        ? showNumber(t.trip_charges_os_night_batta_total_amount)
        : 0;

    // Total batta for this duty
    let batta_main = batta_local + batta_os_day + batta_os_night;

    const extra_hour = showNumber(t.trip_charges_extra_hour);
    const extra_hour_rate = showNumber(t.trip_charges_slab_extra_hour_rate);
    const extra_km = showNumber(t.trip_charges_extra_kms);
    const extra_km_rate = showNumber(t.trip_charges_slab_extra_km_rate);

    // ---------------- DISPLAY FIELDS ----------------
    const displayExtraHours =
      dutyVal === "Outstation" || dutyVal === "OSDayRate"
        ? "-"
        : extra_hour > 0 && extra_hour_rate > 0
          ? `${extra_hour} × ${extra_hour_rate}`
          : "-";

    const displayExtraHoursAmt =
      dutyVal === "Outstation" || dutyVal === "OSDayRate"
        ? "-"
        : showAmount(t.trip_charges_extra_hour_amount);

    const displayExtraKms =
      dutyVal === "Outstation" || dutyVal === "OSDayRate"
        ? "-"
        : extra_km > 0 && extra_km_rate > 0
          ? `${extra_km} × ${extra_km_rate}`
          : "-";

    const displayExtraKmsAmt =
      dutyVal === "Outstation" || dutyVal === "OSDayRate"
        ? "-"
        : showAmount(t.trip_charges_extra_km_amount);

    // ---------------- PARKING / TOLL / PERMIT ----------------
    const parkingNum = showNumber(t.trip_charges_parking);
    const tollNum = showNumber(t.trip_charges_toll_fasttag);
    const permitNum = showNumber(t.trip_charges_permit);
    const othersNum = showNumber(t.trip_charges_others);

    // ---------------- RETURN FINAL ----------------
    return {
      serial: t.serial,

      duty: t.duty,
      tripsheet_number: t.tripsheet_number,
      // open_date: t.open_date
      //   ? new Date(t.open_date).toLocaleDateString("en-GB")
      //   : "-",

      open_date: t.open_date
        ? new Date(normalizeDateAny(t.open_date)).toLocaleDateString("en-GB")
        : "-",

      vehicle_no: t.is_vendor_tripsheet ? t.vendor_vehicle_no : t.vehicleid,
      vehicle_type: t.assigned_vehicle_group_name || "-",

      days: showDash(t.trip_charges_days_used),
      hours: showDash(t.trip_charges_hours_used),
      kms: showDash(t.trip_charges_kms_used),

      slab_display: formatSlabDisplay(t),

      // NUMERIC VALUES (for totals)
      slab_rate: showNumber(
        dutyVal === "Outstation"
          ? t.trip_charges_slab_os_rate
          : t.trip_charges_slab_rate,
      ),

      slab_amount: showNumber(t.trip_charges_charge_amount),
      extra_hour_amount: showNumber(t.trip_charges_extra_hour_amount),
      extra_km_amount: showNumber(t.trip_charges_extra_km_amount),

      // DISPLAY VALUES (for table)
      extra_hours_display: displayExtraHours,
      extra_hour_amount_display: displayExtraHoursAmt,

      extra_kms_display: displayExtraKms,
      extra_km_amount_display: displayExtraKmsAmt,

      batta_local: showNumber(batta_local),
      batta_os_day: showNumber(batta_os_day),
      batta_os_night: showNumber(batta_os_night),

      // This is what HTML uses as N.Batta
      nbatta: showNumber(batta_os_night),

      batta_main: showNumber(batta_main),

      parking: parkingNum,
      toll: tollNum,
      permit: permitNum,
      others: othersNum,

      escort_amount: showNumber(t.trip_charges_escort_amount),

      advance:
        showNumber(t.trip_charges_advance) +
        showNumber(t.trip_charges_booking_advance),

      trip_net_amount:
        showNumber(t.trip_charges_charge_amount) +
        showNumber(t.trip_charges_extra_hour_amount) +
        showNumber(t.trip_charges_extra_km_amount) +
        showNumber(batta_main) +
        parkingNum +
        tollNum +
        permitNum +
        othersNum +
        showNumber(t.trip_charges_escort_amount),

      trip_advance:
        showNumber(t.trip_charges_advance) +
        showNumber(t.trip_charges_booking_advance),
    };
  }

  function mapBankDetails(clientBanks) {
    const bank = clientBanks.find((b) => b.is_primary) || clientBanks[0] || {};

    return {
      bank_name: bank.bank_name || "",
      account_number: bank.account_number || "",
      ifsc: bank.ifsc_code || "", // ✔ now correct
      branch: bank.branch || "",
      account_holder: bank.account_holder_name || "",
    };
  }

  const handlePreviewClose = () => {
    setShowPreview(false);

    // If user confirmed print earlier, now close the invoice also
    if (onSuccess) onSuccess();
  };

  // ========================
  // HANDLE SAVE (UPDATED)
  // ========================
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      if (!form.customerid) {
        setSnackbar({
          open: true,
          message: "Please select a customer",
          severity: "error",
        });
        return;
      }
      if (selectedIndex.length === 0) {
        setSnackbar({
          open: true,
          message: "Please select at least one duty to invoice",
          severity: "warning",
        });
        return;
      }
      if (!clientId) {
        setSnackbar({
          open: true,
          message: "Client ID missing",
          severity: "error",
        });
        return;
      }
      if (!form) {
        setSnackbar({
          open: true,
          message: "Form data missing",
          severity: "error",
        });
        return;
      }

      let invoiceNumber = form.number;

      // -----------------------------
      // 🟦 Generate invoice number
      // -----------------------------
      if (!form.invoiceid) {
        try {
          const createdDoc = await createDocument(
            clientId,
            "Invoice",
            "Invoice Document",
          );
          invoiceNumber = createdDoc.document_number;
        } catch (err) {
          console.error("Error generating invoice number:", err);
          setSnackbar({
            open: true,
            message: "Failed to generate Invoice number",
            severity: "error",
          });
          return;
        }
      }

      const sanitize = (val) => (val === "" ? null : val);

      const invoiceData = {
        ...form,
        client_id: clientId,
        fyid: 1,
        bill_type: "Local",

        invoice_number: invoiceNumber,
        invoice_date: form.date
          ? new Date(form.date).toISOString().slice(0, 10)
          : null,

        customer_id: sanitize(form.customerid),
        basic_amount,
        escort_amount,
        batta_amount,
        parking_amount,
        others_amount,
        permit_amount,
        toll_amount,
        fuel_esc_amount,
        addition_amount: sanitize(form.addition_amount),
        deduction_amount: sanitize(form.deduction_amount),
        discount_percent: sanitize(form.discount_percent),
        discount_amount: sanitize(form.discount_amount),
        net_amount: netAmount,

        voucher_ids: selectedVoucherIds || [],
        voucher_advance_amount: form.voucher_advance_amount || 0,

        is_sentmail: false,
        sentemailid: "",
        is_printed: false,
        is_cancelled: false,
        is_deleted: false,
        client_place_of_supply: sanitize(form.client_place_of_supply),
        place_of_supply: sanitize(form.place_of_supply),
        po_no: sanitize(form.po_no),
        payment_mode: sanitize(form.payment_mode),
        gst_treatment_id: form.gst_treatment_id,
        gst_notification: form.gst_notification,
      };

      let invoiceid;

      // -----------------------------
      // SAVE / UPDATE
      // -----------------------------
      try {
        if (form.invoiceid) {
          await axiosInstance.put(
            `/api/invoices/${form.invoiceid}`,
            invoiceData,
            { params: { client_id: clientId } },
          );
          invoiceid = form.invoiceid;

          setSnackbar({
            open: true,
            message: "Invoice Updated Successfully",
            severity: "success",
          });
        } else {
          const response = await axiosInstance.post(
            "/api/invoices",
            invoiceData,
            { params: { client_id: clientId } },
          );

          invoiceid = response.data.invoiceId || response.data.invoiceid;

          setSnackbar({
            open: true,
            message: "Invoice Saved Successfully",
            severity: "success",
          });

          setForm((prev) => ({ ...prev, invoiceid }));
        }
      } catch (err) {
        const responseData = err.response?.data;

        console.error("Error saving invoice:", responseData || err.message);

        setSnackbar({
          open: true,
          message:
            responseData?.message ||
            responseData?.error ||
            err.message ||
            "Failed to save invoice.",
          severity: "error",
        });
        return;
      }
      // -----------------------------
      // Build selected duty IDs (from your table selection)
      // -----------------------------
      const selectedDutyIdsFromTable = (selectedIndex || [])
        .map((i) => rows[i]?.trip_duty_id)
        .filter((id) => Number.isInteger(id));

      // -----------------------------
      // GET CURRENT LINKS (robust)
      // -----------------------------
      let currentlyLinkedIds = [];
      try {
        const linkedResponse = await axiosInstance.get(
          `/api/invoices/${invoiceid}/tripduties`,
          {
            params: { client_id: clientId, customer_id: form.customerid },
          },
        );

        const payload = linkedResponse.data;

        if (Array.isArray(payload)) {
          currentlyLinkedIds = payload
            .map((d) => d.trip_duty_id)
            .filter(Number.isInteger);
        } else if (payload && Array.isArray(payload.selected)) {
          currentlyLinkedIds = payload.selected
            .map((d) => d.trip_duty_id)
            .filter(Number.isInteger);
        } else {
          console.warn("Unexpected tripduties payload shape:", payload);
          currentlyLinkedIds = [];
        }
      } catch (err) {
        console.error("Error fetching linked trip duties:", err);
        currentlyLinkedIds = [];
      }

      // Unlinking: those currently linked but now unchecked
      const dutyTripIdsToUnlink = currentlyLinkedIds.filter(
        (id) => !selectedDutyIdsFromTable.includes(id),
      );
      const validUnlinkIds = dutyTripIdsToUnlink.filter((id) =>
        Number.isInteger(id),
      );

      if (validUnlinkIds.length > 0) {
        try {
          await axiosInstance.post("/api/invoices/trip-duties/update-invoice", {
            invoiceid: null,
            duty_trip_ids: validUnlinkIds,
          });
        } catch (err) {
          console.error("Error unlinking trip duties:", err);
          setSnackbar({
            open: true,
            message: "Failed to unlink some duties (see console)",
            severity: "warning",
          });
        }
      }

      // Linking: those checked now but previously not linked
      const validSelectedIds = selectedDutyIdsFromTable.filter((id) =>
        Number.isInteger(id),
      );

      if (validSelectedIds.length > 0) {
        try {
          await axiosInstance.post("/api/invoices/trip-duties/update-invoice", {
            invoiceid,
            duty_trip_ids: validSelectedIds,
          });
        } catch (err) {
          console.error("Error linking trip duties:", err);
          setSnackbar({
            open: true,
            message: "Failed to link some duties (see console)",
            severity: "warning",
          });
        }
      }

      /* ============================================================
       VOUCHER LINKING (keeps your existing logic)
       ============================================================ */
      try {
        const selectedRes = await axiosInstance.get("/api/vouchers/selected", {
          params: {
            client_id: clientId,
            invoice_id: invoiceid,
            customer_id: form.customerid,
          },
        });

        const previouslyLinked = (selectedRes.data || []).map(
          (v) => v.voucher_id || v.id,
        );

        const prevSet = previouslyLinked
          .map((x) => parseInt(x, 10))
          .filter((x) => !isNaN(x));

        const newSet = (selectedVoucherIds || [])
          .map((x) => parseInt(x, 10))
          .filter((x) => !isNaN(x));

        const finalVoucherIds = Array.from(new Set(newSet));

        await axiosInstance.post("/api/vouchers/update-linked-vouchers", {
          invoice_id: invoiceid,
          voucher_ids: finalVoucherIds,
          customer_id: form.customerid,
          ledger_id: form.customer_ledger_id,
        });
      } catch (err) {
        console.error("❌ Error linking vouchers:", err);
        setSnackbar({
          open: true,
          message: "Voucher linking failed",
          severity: "error",
        });
        return;
      }

      // -----------------------------------------------------
      // PREVIEW DATA GENERATION (robust to response shape)
      // -----------------------------------------------------
      try {
        const previewTrips = await axiosInstance.get(
          `/api/invoices/${invoiceid}/tripduties`,
          {
            params: { client_id: clientId, customer_id: form.customerid },
          },
        );

        const payload = previewTrips.data;
        const previewSelectedArray = Array.isArray(payload)
          ? payload
          : payload?.selected || [];

        const mappedTrips = previewSelectedArray.map((t, i) =>
          mapTripDuty({ ...t, serial: i + 1 }),
        );

        let basicTotal = 0;
        let battaTotal = 0;
        let parkingTotal = 0;
        let tollTotal = 0;
        let permitTotal = 0;
        let othersTotal = 0;
        let escortTotal = 0;
        let advanceTotal = 0;
        let netTotal = 0;
        let grossTotal = 0;

        mappedTrips.forEach((t) => {
          const basic =
            Number(t.slab_amount || 0) +
            Number(t.extra_hour_amount || 0) +
            Number(t.extra_km_amount || 0);

          basicTotal += basic;

          battaTotal += Number(t.batta_main || 0);
          parkingTotal += Number(t.parking || 0);
          tollTotal += Number(t.toll || 0);
          permitTotal += Number(t.permit || 0);
          othersTotal += Number(t.others || 0);
          escortTotal += Number(t.escort_amount || 0);
          advanceTotal += Number(t.advance || 0);
          netTotal += Number(t.net_amount || 0);

          grossTotal +=
            basic +
            Number(t.batta_main || 0) +
            Number(t.parking || 0) +
            Number(t.toll || 0) +
            Number(t.permit || 0) +
            Number(t.others || 0) +
            Number(t.escort_amount || 0);
        });

        const customerDetails = customers.find(
          (c) => c.customerid == form.customerid,
        );

        const bank = mapBankDetails(clientBanks || []);

        let termsList = [];
        try {
          const termsRes = await axiosInstance.get("/api/terms", {
            params: {
              client_id: clientId,
              document_type: "invoice",
            },
          });

          termsList = termsRes.data?.terms || [];
        } catch (err) {
          console.error("⚠️ Could not load terms:", err);
          termsList = [];
        }

        const previewObject = {
          client_id: clientId,
          template_id: invoiceSettings?.local_invoice_template_id || null,
          client: clientDetails,
          customer: customerDetails,
          invoice: {
            title: invoiceSettings?.invoice_title || "Invoice",
            number: invoiceData.invoice_number,
            date: invoiceData.invoice_date,
            po_no: invoiceData.po_no,
            reference: invoiceData.reference,
          },
          trips: mappedTrips,
          totals: {
            before_tax: form.gross_before_tax || 0,
            taxableamount: form.taxable_amount || 0,
            cgst: form.cgst_amount || 0,
            sgst: form.sgst_amount || 0,
            igst: form.igst_amount || 0,
            cgst_percent: form.cgst_percent || 0,
            sgst_percent: form.sgst_percent || 0,
            igst_percent: form.igst_percent || 0,
            fuel_escalation: form.fuel_esc_amount || 0,
            fuel_ex_remarks: form.fuel_ex_remarks || "",
            any_addition: form.addition_amount || 0,
            addition_remarks: form.addition_remarks || "",
            any_deduction: form.deduction_amount || 0,
            deduction_remarks: form.deduction_remarks || "",
            discount: form.discount_amount || 0,
            discount_percent: form.discount_percent || 0,
            advance:
              (form.advance_amount || 0) + (form.voucher_advance_amount || 0),
            net: netAmount || 0,
            word_amount: numberToIndianWords(netAmount || 0),
          },

          gst: {
            gst_treatment: form.gst_treatment,
            gst_treatment_id: form.gst_treatment_id,
            gst_notification: form.gst_notification,
            hsn_code: form.hsn_code,
          },

          totalsBreakup: {
            basic: basicTotal,
            batta: battaTotal,
            parking: parkingTotal,
            toll: tollTotal,
            permit: permitTotal,
            others: othersTotal,
            escort: escortTotal,
            advance: advanceTotal,
            net: netTotal,
            gross: grossTotal,
          },

          bank: bank,
          terms: termsList,
        };

        console.log("PREVIEW_OBJECT", JSON.stringify(previewObject));
        try {
          const previewRes = await axiosInstance.post(
            "/api/document-template/invoice/preview/local",
            previewObject,
          );

          setPreviewHTML(previewRes.data.html);
          setPreviewData(previewObject);

          setTimeout(() => setShowPreview(true), 100);
        } catch (e) {
          console.error("Preview HTTP status:", e.response?.status);
          console.error("Preview server response:", e.response?.data);
          console.error("Preview error message:", e.message);

          setSnackbar({
            open: true,
            message:
              e.response?.data?.error ||
              e.response?.data?.message ||
              e.message ||
              "Preview generation failed",
            severity: "error",
          });
          return;
        }
      } catch (error) {
        console.error("Fatal error in handleSave:", error);
        setSnackbar({
          open: true,
          message:
            "Fatal error saving invoice: " + (error.message || "Unknown error"),
          severity: "error",
        });
      }
    } catch (outerError) {
      console.error("Unexpected error in handleSave:", outerError);
      setSnackbar({
        open: true,
        message: "Unexpected error saving invoice",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel(); // Notify parent to hide the form
    // Optionally reset form state here if needed
  };

  const denseCellStyle = {
    border: "1px solid #ccc",
    px: 1, // Tight horizontal padding
    py: 0.5, // Tight vertical padding
    fontSize: 13,
    minWidth: 70,
  };

  const fontSizeUnified = 14; // Set your preferred font size here
  const fieldHeight = "28px";
  const fieldFontSize = fontSizeUnified;
  const columnWidths = {
    // label: 200,
    // inputLarge: 360,
    // inputMedium: 222,
    // inputSmall: 240,

    label: 400,
    inputLarge: 300,
    inputMedium: 400,
    inputSmall: 200,
  };

  const inputSx = {
    fontSize: fieldFontSize,
    height: fieldHeight,
    "& .MuiInputBase-input": {
      paddingTop: "0px",
      paddingBottom: "0px",
      fontSize: fieldFontSize,
      height: fieldHeight,
      lineHeight: fieldHeight,
      textAlign: "right",
    },
  };

  const inputtext = {
    fontSize: fieldFontSize,
    height: fieldHeight,
    "& .MuiInputBase-input": {
      paddingTop: "0px",
      paddingBottom: "0px",
      fontSize: fieldFontSize,
      height: fieldHeight,
      lineHeight: fieldHeight,
      textAlign: "left",
    },
  };

  const rowBg = (index) => (index % 2 === 0 ? "#ffffff" : "#f5f7fa");

  const UniversalField = ({ label, value, sx = {}, ...rest }) => (
    <TextField
      size="small"
      label={label}
      value={value}
      sx={{
        ...inputSx,
        width: "100%",
        "& .MuiInputBase-root": {
          height: fieldHeight, // fixed height
          minHeight: fieldHeight,
        },
        "& .MuiInputBase-input": {
          height: fieldHeight,
          lineHeight: fieldHeight,
          fontSize: fieldFontSize,
          textAlign: "right", // 🔥 FORCE RIGHT ALIGN
        },
        ...sx, // external styles allowed
      }}
      InputProps={{ readOnly: true }}
      {...rest}
    />
  );

  const safeValue = (v) =>
    typeof v === "number"
      ? v.toFixed(2)
      : !isNaN(parseFloat(v))
        ? parseFloat(v).toFixed(2)
        : "0.00";

  return (
    <Box sx={{ maxWidth: 1300, mx: "auto", mt: 4, mb: 4, p: 2 }}>
      {/* Header fields */}
      <Typography
        sx={{ fontWeight: "bold", color: "#226ad3", mb: 2, fontSize: 17 }}
      >
        Invoice Details
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        {/* <TextField
          label="Client Place of Supply"
          value={form.client_place_of_supply}
          InputProps={{ readOnly: true }}
          size="small"
          variant="outlined"
          sx={{ mb: 1, minWidth: 240 }}
        /> */}

        <TextField
          size="small"
          variant="outlined"
          name="number"
          label="Invoice Number"
          value={form.number}
          onChange={handleFieldChange}
          sx={{ minWidth: 180, background: "#fcfcfc" }}
        />
        <MyDatePicker
          label="Invoice Date"
          value={form.date}
          onChange={(date) => {
            if (!date) {
              setForm((prev) => ({ ...prev, date: "" }));
              return;
            }
            const d = new Date(date);
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");
            setForm((prev) => ({ ...prev, date: `${yyyy}-${mm}-${dd}` }));
          }}
          placeholder="DD/MM/YYYY"
          containerStyle={{ marginBottom: 0 }}
          inputStyle={{ minWidth: 160, background: "#fcfcfc" }}
          labelStyle={{ fontSize: 12 }}
        />

        <TextField
          size="small"
          variant="outlined"
          name="reference"
          label="Reference"
          value={form.reference}
          onChange={handleFieldChange}
          sx={{ minWidth: 160, background: "#fcfcfc" }}
        />
        <TextField
          size="small"
          variant="outlined"
          name="po_no"
          label="P.O. Number"
          value={form.po_no}
          onChange={handleFieldChange}
          sx={{ minWidth: 160, background: "#fcfcfc" }}
        />
        <TextField
          select
          size="small"
          variant="outlined"
          name="payment_mode"
          label="Mode"
          value={form.payment_mode}
          onChange={handleFieldChange}
          sx={{ minWidth: 220, background: "#fcfcfc" }}
        >
          <MenuItem value="">Select</MenuItem>
          {paymentModes.map((payment_mode) => (
            <MenuItem key={payment_mode} value={payment_mode}>
              {payment_mode}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
        <FormControl size="small" sx={{ width: 655 }}>
          <InputLabel shrink>Customer</InputLabel>

          <CustomerDropdown
            customers={customers}
            selectedCustomerId={form.customerid}
            onChange={(val) => {
              const selected = customers.find(
                (c) => String(c.customerid) === String(val),
              );

              if (!selected) return;

              console.log("Selected customer:", selected);

              setForm((prev) => ({
                ...prev,
                customerid: selected.customerid,
                customerName: selected.customername,
                gst_treatment: selected.customergsttreament,
                tax_preference: selected.customertaxpreference,
                customergstin: selected.customergstin,
                place_of_supply: selected.customerplaceofsupply,
                cal_gst_parking_toll_permit:
                  selected.customerapplygstparkingtollpermit,
                cal_gst_batta: selected.customerapplygstbatta,
                cal_gst_others: selected.customerapplygstothers,
              }));

              // Trigger useEffect to fetch trip duties
              setSelectedCustomer(selected.customerid);

              // // 🔥 FIX: Load duties immediately for NEW invoice
              // if (!form.invoiceid || form.invoiceid === "0") {
              //   console.log("🟦 NEW invoice → loading duties...");
              //   handleAddDutyClick();  // <-- THIS FIXES YOUR ISSUE
              // }
            }}
          />
        </FormControl>
        <TextField
          size="small"
          variant="outlined"
          name="customergstin"
          label="GSTIN"
          value={form.customergstin || ""}
          InputProps={{ readOnly: true }}
          sx={{ minWidth: 160, background: "#fcfcfc", mr: 0 }}
        />
        <TextField
          size="small"
          variant="outlined"
          name="place_of_supply"
          label="Place of Supply"
          value={form.place_of_supply || ""}
          InputProps={{ readOnly: true }}
          sx={{ minWidth: 160, background: "#fcfcfc", mr: 0 }}
        />
      </Box>

      {/* filter section */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 0 }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
          Filter By:
        </Typography>
        <RadioGroup row value={filterType} onChange={handleFilterChange}>
          <FormControlLabel
            value="all"
            control={<Radio size="small" />}
            label="All"
            sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
          />
          <FormControlLabel
            value="date"
            control={<Radio size="small" />}
            label="Date"
            sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
          />
        </RadioGroup>

        {filterType === "date" && (
          <Stack direction="row" spacing={2}>
            <MyDatePicker
              label="From"
              value={fromDate}
              onChange={(date) => setFromDate(date)}
              containerStyle={{ marginBottom: 0 }}
              inputStyle={{ minWidth: 140 }}
              labelStyle={{ fontSize: 12 }}
            />
            <MyDatePicker
              label="To"
              value={toDate}
              onChange={(date) => setToDate(date)}
              containerStyle={{ marginBottom: 0 }}
              inputStyle={{ minWidth: 140 }}
              labelStyle={{ fontSize: 12 }}
            />
          </Stack>
        )}
      </Box>

      {/* ----- Table Section ------- */}

      <Box
        sx={{
          mt: 1,
          border: "1px solid #ddd",
          overflowX: "auto",
          borderRadius: 1,
        }}
      >
        {isEditMode && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleAddDutyClick}
            >
              +Duty
            </Button>
          </Box>
        )}

        <Table
          size="small"
          stickyHeader
          sx={{ borderCollapse: "collapse", borderRadius: "8px" }}
        >
          <TableHead sx={{ backgroundColor: "#e0e3e4ff" }}>
            <TableRow
              sx={{
                "& th": {
                  backgroundColor: "#e0e3e4ff",
                  fontWeight: "bold",
                },
              }}
            >
              <TableCell
                padding="none"
                sx={{
                  ...denseCellStyle,
                  width: 36,
                  minWidth: 36,
                  maxWidth: 36,
                  p: 0,
                  m: 0,
                  textAlign: "center",
                }}
              >
                <Checkbox
                  size="small"
                  checked={selectedAll}
                  onChange={handleSelectAll}
                  sx={{ p: 0, m: 0 }}
                  inputProps={{ "aria-label": "select all rows" }}
                />
              </TableCell>

              <TableCell
                sx={{
                  ...denseCellStyle,
                  width: 100,
                  minWidth: 100,
                  maxWidth: 120,
                }}
              >
                TS No
              </TableCell>
              <TableCell
                sx={{
                  ...denseCellStyle,
                  width: 80,
                  minWidth: 80,
                  maxWidth: 90,
                }}
              >
                Open Date
              </TableCell>
              <TableCell sx={denseCellStyle}>Duty</TableCell>
              <TableCell sx={denseCellStyle}>Location</TableCell>
              <TableCell
                sx={{
                  ...denseCellStyle,
                  width: 110,
                  minWidth: 110,
                  maxWidth: 130,
                }}
              >
                Veh No,
              </TableCell>
              <TableCell
                sx={{
                  ...denseCellStyle,
                  width: 100,
                  minWidth: 100,
                  maxWidth: 130,
                }}
              >
                Veh Group
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...denseCellStyle,
                  width: 100,
                  minWidth: 100,
                  maxWidth: 130,
                }}
              >
                Basic
              </TableCell>
              <TableCell align="right" sx={denseCellStyle}>
                Fuel
              </TableCell>
              <TableCell align="right" sx={denseCellStyle}>
                Escort
              </TableCell>
              <TableCell align="right" sx={denseCellStyle}>
                Batta
              </TableCell>
              <TableCell align="right" sx={denseCellStyle}>
                Parking
              </TableCell>
              <TableCell align="right" sx={denseCellStyle}>
                Toll
              </TableCell>
              <TableCell align="right" sx={denseCellStyle}>
                Permit
              </TableCell>
              <TableCell align="right" sx={denseCellStyle}>
                Others
              </TableCell>
              <TableCell align="right" sx={denseCellStyle}>
                Advance
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, idx) => (
              <TableRow
                key={`${row.trip_duty_id}-${idx}`}
                selected={selectedIndex.includes(idx)}
                hover
              >
                <TableCell
                  padding="none"
                  sx={{
                    border: "1px solid #ddd",
                    width: 36,
                    height: 36,
                    verticalAlign: "middle",
                    textAlign: "center",
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={selectedIndex.includes(idx)}
                    onChange={() => handleSelectRow(idx)}
                    sx={{ p: 0, m: 0 }}
                  />
                </TableCell>

                <TableCell sx={denseCellStyle}>
                  {row.tripsheet_number}
                </TableCell>

                <TableCell sx={{ ...denseCellStyle, width: 100, p: 1, pr: 1 }}>
                  <div
                    style={{
                      padding: 0,
                      margin: 0,
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {row.open_date
                      ? new Date(row.open_date).toLocaleDateString("en-GB")
                      : ""}
                  </div>
                </TableCell>

                <TableCell sx={denseCellStyle}>
                  {row.duty}
                  {row.billing_status === "review" && (
                    <span
                      style={{
                        color: "#ef8f00",
                        marginLeft: 6,
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      Driver closed
                    </span>
                  )}
                </TableCell>
                <TableCell sx={denseCellStyle}>{row.location}</TableCell>

                <TableCell
                  sx={{
                    ...denseCellStyle,
                    width: 120,
                    minWidth: 120,
                    maxWidth: 150,
                  }}
                >
                  {row.vehicleid
                    ? row.vehicleid
                    : row.vendorid
                      ? row.vendorid
                      : "-"}
                </TableCell>

                <TableCell
                  sx={{
                    ...denseCellStyle,
                    width: 100,
                    minWidth: 100,
                    maxWidth: 150,
                  }}
                >
                  {row.assigned_vehicle_group_name}
                </TableCell>

                {/* ===== AMOUNT COLUMNS (Supports RAW + MAPPED) ===== */}

                <TableCell sx={denseCellStyle} align="right">
                  {safeValue(row.trip_charges_basic_amount ?? row.slab_amount)}
                </TableCell>

                <TableCell sx={denseCellStyle} align="right">
                  {safeValue(row.trip_charges_fuel_esculation_amount ?? 0)}
                </TableCell>

                <TableCell sx={denseCellStyle} align="right">
                  {safeValue(
                    row.trip_charges_escort_amount ?? row.escort_amount,
                  )}
                </TableCell>

                <TableCell sx={denseCellStyle} align="right">
                  {safeValue(getRowBatta(row))}
                </TableCell>

                <TableCell sx={denseCellStyle} align="right">
                  {safeValue(row.trip_charges_parking ?? row.parking)}
                </TableCell>

                <TableCell sx={denseCellStyle} align="right">
                  {safeValue(row.trip_charges_toll_fasttag ?? row.toll)}
                </TableCell>

                <TableCell sx={denseCellStyle} align="right">
                  {safeValue(row.trip_charges_permit ?? 0)}
                </TableCell>

                <TableCell sx={denseCellStyle} align="right">
                  {safeValue(row.trip_charges_others ?? row.others)}
                </TableCell>

                <TableCell sx={denseCellStyle} align="right">
                  {safeValue(row.trip_charges_advance ?? row.advance)}
                </TableCell>
              </TableRow>
            ))}

            {/* Totals row */}
            <TableRow sx={{ backgroundColor: "#f3f6fb" }}>
              <TableCell colSpan={2} sx={{ border: "1px solid #bbb" }}>
                <Typography sx={{ color: "#009800", fontWeight: "bold" }}>
                  {selectedIndex.length} : Selected
                </Typography>
              </TableCell>

              <TableCell sx={{ border: "1px solid #bbb" }} />
              <TableCell sx={{ border: "1px solid #bbb" }} />
              <TableCell sx={{ border: "1px solid #bbb" }} />
              <TableCell sx={{ border: "1px solid #bbb" }} />
              <TableCell
                sx={{
                  border: "1px solid #bbb",
                  textAlign: "right",
                  fontWeight: "bold",
                }}
              >
                Totals:
              </TableCell>

              {/* TOTALS SUPPORT RAW + MAPPED */}
              {[
                { raw: "trip_charges_basic_amount", map: "slab_amount" },
                { raw: "trip_charges_fuel_esculation_amount", map: null },
                { raw: "trip_charges_escort_amount", map: "escort_amount" },
                {
                  raw: "trip_charges_batta",
                  map: "batta_main",
                  get: getRowBatta,
                }, // ✅
                { raw: "trip_charges_parking", map: "parking" },
                { raw: "trip_charges_toll_fasttag", map: "toll" },
                { raw: "trip_charges_permit", map: null },
                { raw: "trip_charges_others", map: "others" },
                { raw: "trip_charges_advance", map: "advance" },
              ].map((field, index) => (
                <TableCell
                  key={`${field.raw}-${index}`}
                  sx={{ border: "1px solid #bbb", fontWeight: "bold" }}
                  align="right"
                >
                  {safeValue(
                    rows.reduce(
                      (acc, r) =>
                        acc +
                        (field.get
                          ? field.get(r)
                          : toNum(r[field.raw] ?? r[field.map] ?? 0)),
                      0,
                    ),
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {/* ===================== +Voucher Button (Top Right) ===================== */}
      {form.customerid && (
        <Box sx={{ textAlign: "right", mt: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleOpenVoucherTable}
            sx={{ fontWeight: "bold" }}
          >
            + Voucher
          </Button>
        </Box>
      )}

      {/* ===================== VOUCHER TABLE ===================== */}
      {showVoucherTable && (
        <Box
          sx={{
            mt: 2,
            border: "1px solid #d0d0d0",
            borderRadius: "6px",
            background: "#fff",
            p: 2,
            overflow: "visible !important",
          }}
        >
          {/* ---------- HEADER WITH +Voucher on Right ---------- */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "16px",
                color: "#333",
              }}
            >
              Select Advance Vouchers
            </Typography>

            {/* ❌ Light simple close icon */}
            <CloseIcon
              onClick={() => setShowVoucherTable(false)}
              sx={{
                cursor: "pointer",
                color: "#999", // light grey
                fontSize: "22px",
                "&:hover": {
                  color: "#555", // slightly darker on hover
                },
              }}
            />
          </Box>

          <Table
            size="small"
            sx={{
              minWidth: 700,
              border: "1px solid #e0e0e0",
              "& th": {
                background: "#f5f5f5",
                fontWeight: "bold",
                fontSize: "14px",
                padding: "4px",
                borderRight: "1px solid #ddd",
              },
              "& td": {
                padding: "4px",
                fontSize: "14px",
                borderBottom: "1px solid #eee",
                borderRight: "1px solid #f0f0f0",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  padding="none"
                  sx={{
                    border: "1px solid #ddd",
                    width: 36,
                    height: 36,
                    verticalAlign: "middle",
                    textAlign: "center",
                  }}
                >
                  <Checkbox
                    checked={voucherSelectAll}
                    onChange={handleVoucherSelectAll}
                    sx={{
                      padding: "2px",
                      "& .MuiSvgIcon-root": {
                        fontSize: "20px",
                      },
                    }}
                  />
                </TableCell>

                <TableCell>Voucher No</TableCell>
                <TableCell>Voucher Date</TableCell>
                <TableCell>Narration</TableCell>
                <TableCell align="right">Advance Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vouchers.map((v) => {
                // FORCE numeric ID here (always a number)
                const vid = Number(v.voucher_id ?? v.id);

                return (
                  <TableRow
                    key={vid}
                    hover
                    selected={selectedVoucherIds.includes(vid)}
                    sx={{
                      cursor: "pointer",
                      "&.Mui-selected": {
                        backgroundColor: "#e8f4ff !important",
                      },
                    }}
                    onClick={(e) => {
                      // 👉 Prevent row click when checkbox is clicked
                      if (e.target.closest(".voucher-checkbox")) return;

                      handleVoucherSelect(vid);
                    }}
                  >
                    <TableCell
                      padding="none"
                      sx={{
                        border: "1px solid #ddd",
                        width: 36,
                        height: 36,
                        verticalAlign: "middle",
                        textAlign: "center",
                      }}
                    >
                      <Checkbox
                        className="voucher-checkbox" // ⭐ identifier to stop row click
                        checked={selectedVoucherIds.includes(vid)}
                        onChange={() => handleVoucherSelect(vid)} // ⭐ checkbox toggle
                        inputProps={{ "aria-label": `select-voucher-${vid}` }}
                        sx={{
                          padding: "2px",
                          "& .MuiSvgIcon-root": { fontSize: "20px" },
                        }}
                      />
                    </TableCell>

                    <TableCell>{v.voucher_number}</TableCell>
                    <TableCell>
                      {new Date(v.voucher_date).toLocaleDateString("en-GB")}
                    </TableCell>
                    <TableCell>{v.narration}</TableCell>
                    <TableCell align="right">
                      {Number(v.credit_amount || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* ------------Summary----------------- */}
      <Box
        sx={{
          mt: 2,
          mb: 4,
          px: 3,
          py: 2,
          maxWidth: 1260,
          background: "#fff",
          borderRadius: 3,
          boxShadow: 2,
          // overflowY: "auto",              // Enables vertical scrolling
          // overflowX: "hidden",            // Hides horizontal overflow
        }}
      >
        {/* Top row */}
        <Stack direction="row" alignItems="center" spacing={0} mb={2}>
          <Box sx={{ width: columnWidths.inputLarge, mr: 2 }}>
            <UniversalField
              label="GST Treatment"
              value={form.gst_treatment}
              sx={{ ...inputtext, width: "100%" }}
            />
          </Box>
          <Box sx={{ width: columnWidths.inputSmall }}>
            <UniversalField
              label="Tax Preference"
              value={form.tax_preference}
              sx={{ ...inputtext, width: "100%" }}
            />
          </Box>
          <Box sx={{ width: columnWidths.inputSmall, ml: 2 }}>
            <UniversalField
              label="HSN Code"
              value={hsn_code}
              sx={{ ...inputtext, width: "100%" }}
            />
          </Box>
          <Box sx={{ width: columnWidths.inputSmall, ml: 2 }} />
        </Stack>

        {/* Charges rows */}
        {/* Charges rows */}
        <Stack spacing={0.25}>
          {[
            { label: "Basic Amount", value: basic_amount },
            { label: "Fuel Escalation", value: fuel_esc_amount },
            { label: "Escort", value: escort_amount },
            { label: "Batta", value: batta_amount, gstField: "cal_gst_batta" },
            {
              label: "Parking",
              value: parking_amount,
              gstField: "cal_gst_parking_toll_permit",
            },
            {
              label: "Toll",
              value: toll_amount,
              gstField: "cal_gst_parking_toll_permit",
            },
            {
              label: "Permit",
              value: permit_amount,
              gstField: "cal_gst_parking_toll_permit",
            },
            {
              label: "Others",
              value: others_amount,
              gstField: "cal_gst_others",
            },
          ].map(({ label, value, gstField }, idx) => (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0}
              key={label}
              sx={{
                minHeight: fieldHeight,
                backgroundColor: rowBg(idx),
                borderRadius: "4px",
              }}
            >
              <Box sx={{ width: columnWidths.label }}>
                <Typography sx={{ fontSize: fieldFontSize }}>
                  {label}
                </Typography>
              </Box>
              <Box sx={{ width: columnWidths.inputLarge }} />
              <Box
                sx={{
                  width: columnWidths.inputMedium,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                {gstField ? (
                  <TextField
                    size="small"
                    variant="outlined"
                    value={form[gstField] || ""}
                    placeholder="Yes / No"
                    InputProps={{ readOnly: true }}
                    sx={{
                      width: 100,
                      mr: 4,
                      "& .MuiInputBase-root": {
                        height: fieldHeight,
                        minHeight: fieldHeight,
                      },
                      "& .MuiInputBase-input": {
                        textAlign: "center",
                        fontSize: fieldFontSize - 1,
                        padding: "0 4px",
                        height: fieldHeight,
                        boxSizing: "border-box",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#bbb",
                      },
                    }}
                    InputLabelProps={{ shrink: false }}
                    inputProps={{ style: { textAlign: "center" } }}
                  />
                ) : null}
              </Box>
              <Box sx={{ width: columnWidths.inputSmall }}>
                <UniversalField value={safeValue(value)} />
              </Box>
            </Stack>
          ))}
        </Stack>

        {/* Any Addition Charges */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0}
          sx={{
            mt: 0.25,
            minHeight: fieldHeight,
            backgroundColor: rowBg(8),

            borderRadius: "4px",
          }}
        >
          <Box sx={{ width: columnWidths.label }}>
            <Typography sx={{ fontSize: fieldFontSize }}>
              Any Addition Charges
            </Typography>
          </Box>
          <Box sx={{ width: columnWidths.inputLarge }}>
            <TextField
              size="small"
              sx={{
                width: "100%",
                "& .MuiInputBase-root": { height: fieldHeight },
                "& .MuiInputBase-input": {
                  fontSize: fieldFontSize,
                  height: fieldHeight,
                  padding: "0 8px",
                  textAlign: "left",
                  boxSizing: "border-box",
                },
              }}
              placeholder="Addition Remarks"
              value={form.addition_remarks || ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  addition_remarks: e.target.value,
                }))
              }
            />
          </Box>
          <Box sx={{ width: columnWidths.inputMedium }} />
          <Box sx={{ width: columnWidths.inputSmall }}>
            <TextField
              size="small"
              sx={{
                width: "100%",
                "& .MuiInputBase-root": { height: fieldHeight },
                "& .MuiInputBase-input": {
                  fontSize: fieldFontSize,
                  height: fieldHeight,
                  padding: "0 8px",
                  textAlign: "right",
                  boxSizing: "border-box",
                  "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button":
                    {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                },
              }}
              type="number"
              step="0.01"
              value={
                form.addition_amount != null
                  ? (Number(form.addition_amount) || 0).toFixed(2)
                  : "0.00"
              }
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  addition_amount: parseFloat(e.target.value) || 0,
                }))
              }
            />
          </Box>
        </Stack>

        {/* Discount (on Basic Amount) */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0}
          sx={{
            mt: 0.25,
            minHeight: fieldHeight,
            backgroundColor: rowBg(9),

            borderRadius: "4px",
          }}
        >
          <Box sx={{ width: columnWidths.label }}>
            <Typography sx={{ fontSize: fieldFontSize }}>
              Discount (on Basic Amount)
            </Typography>
          </Box>
          <Box sx={{ width: columnWidths.inputLarge }}>
            <TextField
              size="small"
              sx={{
                width: 185,
                "& .MuiInputBase-root": { height: fieldHeight },
                "& .MuiInputBase-input": {
                  fontSize: fieldFontSize,
                  height: fieldHeight,
                  padding: "0 8px",
                  textAlign: "right",
                  boxSizing: "border-box",
                },
              }}
              type="number"
              value={form.discount_percent || 0}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  discount_percent: Number(e.target.value) || 0,
                }))
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      sx={{ fontWeight: "bold", fontSize: fieldFontSize }}
                    >
                      %
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ width: columnWidths.inputMedium }} />
          <Box sx={{ width: columnWidths.inputSmall }}>
            <UniversalField
              value={
                form.discount_amount != null
                  ? (Number(form.discount_amount) || 0).toFixed(2)
                  : "0.00"
              }
            />
          </Box>
        </Stack>

        {/* Any Deduction */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0}
          sx={{
            mt: 0.25,
            minHeight: fieldHeight,
            backgroundColor: rowBg(10),

            borderRadius: "4px",
          }}
        >
          <Box sx={{ width: columnWidths.label }}>
            <Typography sx={{ fontSize: fieldFontSize }}>
              Any Deduction
            </Typography>
          </Box>
          <Box sx={{ width: columnWidths.inputLarge }}>
            <TextField
              size="small"
              sx={{
                width: "100%",
                "& .MuiInputBase-root": { height: fieldHeight },
                "& .MuiInputBase-input": {
                  fontSize: fieldFontSize,
                  height: fieldHeight,
                  padding: "0 8px",
                  textAlign: "left",
                  boxSizing: "border-box",
                },
              }}
              placeholder="Deduction Remarks"
              value={form.deduction_remarks || ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  deduction_remarks: e.target.value,
                }))
              }
            />
          </Box>
          <Box sx={{ width: columnWidths.inputMedium }} />
          <Box sx={{ width: columnWidths.inputSmall }}>
            <TextField
              size="small"
              sx={{
                width: "100%",
                "& .MuiInputBase-root": { height: fieldHeight },
                "& .MuiInputBase-input": {
                  fontSize: fieldFontSize,
                  height: fieldHeight,
                  padding: "0 8px",
                  textAlign: "right",
                  boxSizing: "border-box",
                  "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button":
                    {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                },
              }}
              type="number"
              step="0.01"
              value={Number(form.deduction_amount).toFixed(2)}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  deduction_amount: parseFloat(e.target.value) || 0,
                }))
              }
            />
          </Box>
        </Stack>

        {/* Amount before tax (Gross) */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0}
          sx={{
            mt: 0.25,
            minHeight: fieldHeight,
            backgroundColor: rowBg(11),

            borderRadius: "4px",
          }}
        >
          <Box sx={{ width: columnWidths.label }}>
            <Typography sx={{ fontWeight: "bold", fontSize: fieldFontSize }}>
              {form.tax_preference === "Taxable"
                ? "Amount before tax (Gross)"
                : "Gross Amount"}
            </Typography>
          </Box>
          <Box sx={{ width: columnWidths.inputLarge }} />
          <Box sx={{ width: columnWidths.inputMedium }} />
          <Box sx={{ width: columnWidths.inputSmall }}>
            <TextField
              size="small"
              sx={{
                width: "100%",
                background: "#d3f5d7",
                "& .MuiInputBase-root": { height: fieldHeight },
                "& .MuiInputBase-input": {
                  fontSize: fieldFontSize,
                  height: fieldHeight,
                  textAlign: "right",
                  boxSizing: "border-box",
                },
              }}
              value={
                form.gross_before_tax
                  ? form.gross_before_tax.toFixed(2)
                  : "0.00"
              }
              inputProps={{ readOnly: true }}
            />
          </Box>
        </Stack>

        {/* Taxable Amount */}
        {/* Taxable Amount */}
        {form.tax_preference === "Taxable" && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0}
            sx={{
              mt: 0.25,
              minHeight: fieldHeight,
              backgroundColor: rowBg(12),

              borderRadius: "4px",
            }}
          >
            <Box sx={{ width: columnWidths.label }}>
              <Typography sx={{ fontSize: fieldFontSize }}>
                Taxable Amount
              </Typography>
            </Box>
            <Box sx={{ width: columnWidths.inputSmall }}>
              <TextField
                size="small"
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": { height: fieldHeight },
                  "& .MuiInputBase-input": {
                    fontSize: fieldFontSize,
                    height: fieldHeight,
                    textAlign: "right",
                    boxSizing: "border-box",
                  },
                }}
                value={
                  form.taxable_amount ? form.taxable_amount.toFixed(2) : "0.00"
                }
                inputProps={{ readOnly: true }}
              />
            </Box>
            <Box sx={{ width: columnWidths.inputLarge }} />
            <Box sx={{ width: columnWidths.inputMedium }} />
          </Stack>
        )}

        {/* GST Rows */}
        {form.tax_preference === "Taxable" &&
          (() => {
            const isRCM =
              form.gst_treatment === "Registered Business-Under RCM";

            const gstRows =
              form.place_of_supply === form.client_place_of_supply
                ? [
                    {
                      type: "CGST",
                      percent: form.cgst_percent,
                      amount: form.cgst_amount,
                    },
                    {
                      type: "SGST",
                      percent: form.sgst_percent,
                      amount: form.sgst_amount,
                    },
                  ]
                : [
                    {
                      type: "IGST",
                      percent: form.igst_percent,
                      amount: form.igst_amount,
                    },
                  ];

            return gstRows.map(({ type, percent, amount }, i) => (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0}
                key={type}
                sx={{
                  mt: 0.25,
                  minHeight: fieldHeight,
                  backgroundColor: rowBg(13 + i),

                  borderRadius: "4px",
                }}
              >
                <Box sx={{ width: columnWidths.label }}>
                  <Typography
                    sx={{
                      fontSize: fieldFontSize,
                      color: isRCM ? "red" : "inherit",
                    }}
                  >
                    {type}
                  </Typography>
                </Box>
                <Box sx={{ width: columnWidths.inputLarge }}>
                  <TextField
                    size="small"
                    name={
                      type === "CGST"
                        ? "cgst_percent"
                        : type === "SGST"
                          ? "sgst_percent"
                          : "igst_percent"
                    }
                    value={percent ?? ""}
                    onChange={handleFieldChange}
                    sx={{
                      width: 185,
                      "& .MuiInputBase-root": { height: fieldHeight },
                      "& .MuiInputBase-input": {
                        fontSize: fieldFontSize,
                        height: fieldHeight,
                        textAlign: "right",
                        boxSizing: "border-box",
                      },
                    }}
                    InputProps={{
                      sx: {
                        "& .MuiInputBase-input": {
                          color: isRCM ? "red" : "inherit",
                        },
                      },
                      endAdornment: (
                        <Typography
                          sx={{
                            color: isRCM ? "red" : "text.secondary",
                            fontSize: fieldFontSize,
                            pr: 1,
                          }}
                        >
                          %
                        </Typography>
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ width: columnWidths.inputMedium }} />
                <Box sx={{ width: columnWidths.inputSmall }}>
                  <UniversalField
                    value={amount != null ? amount.toFixed(2) : "0.00"}
                    sx={{
                      "& .MuiInputBase-input": {
                        color: isRCM ? "red" : "inherit",
                        textAlign: isRCM ? "left" : "right",
                      },
                    }}
                  />
                </Box>
              </Stack>
            ));
          })()}

        {/* Advance and other amount rows */}
        {/* Advance and other amount rows */}
        <Stack spacing={0.25} sx={{ mt: 0.25 }}>
          {[
            { label: "Advance in Tripsheet", value: form.advance_amount },
            {
              label: "Advance in Receipts (On Account)",
              value: form.voucher_advance_amount,
            },
            { label: "Coin Adjust", value: form.coin_adjust },
            { label: "Net Amount", value: netAmount, highlight: true },
          ].map(({ label, value, highlight }, idx) => (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0}
              key={label}
              sx={{
                minHeight: fieldHeight,
                backgroundColor: rowBg(15 + idx),

                borderRadius: "4px",
              }}
            >
              <Box sx={{ width: columnWidths.label }}>
                <Typography
                  sx={{
                    fontSize: fieldFontSize,
                    fontWeight: highlight ? "bold" : "normal",
                  }}
                >
                  {label}
                </Typography>
              </Box>
              <Box sx={{ width: columnWidths.inputLarge }} />
              <Box sx={{ width: columnWidths.inputMedium }} />
              <Box sx={{ width: columnWidths.inputSmall }}>
                <TextField
                  size="small"
                  sx={{
                    width: "100%",
                    background: highlight ? "#d3f5d7" : undefined,
                    "& .MuiInputBase-root": { height: fieldHeight },
                    "& .MuiInputBase-input": {
                      fontSize: fieldFontSize,
                      height: fieldHeight,
                      textAlign: "right",
                      fontWeight: highlight ? "bold" : "normal",
                      boxSizing: "border-box",
                    },
                  }}
                  value={value != null ? value.toFixed(2) : "0.00"}
                  inputProps={{ readOnly: true }}
                />
              </Box>
            </Stack>
          ))}
        </Stack>

        {/* Bottom Remarks */}
        <Stack direction="row" spacing={2} mt={1.5}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            maxRows={2}
            label="GST Notification"
            name="gst_notification"
            value={form.gst_notification || ""}
            onChange={handleFieldChange}
            sx={{
              minWidth: 220,
              "& .MuiInputBase-input": {
                textAlign: "left",
                padding: "6px 8px",
                fontSize: "12px",
                lineHeight: "16px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "12px",
              },
              "& textarea": {
                textAlign: "left !important",
              },
            }}
          />

          <TextField
            fullWidth
            size="small"
            label="Remarks"
            name="remarks"
            multiline
            minRows={2}
            maxRows={2}
            value={form.remarks || ""}
            onChange={handleFieldChange}
            sx={{
              minWidth: 220,
              "& .MuiInputBase-input": {
                textAlign: "left",
                padding: "6px 8px",
                fontSize: "12px",
                lineHeight: "16px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "12px",
              },
              "& textarea": {
                textAlign: "left !important",
              },
            }}
          />
        </Stack>
      </Box>

      <Box
        sx={{ display: "flex", justifyContent: "flex-start", mt: 3, gap: 2 }}
      >
        <Button variant="contained" color="primary" onClick={handleSave}>
          {form.invoiceid && form.invoiceid !== "new" ? "Update" : "Save"}
        </Button>
        <Button variant="outlined" color="secondary" onClick={handleCancel}>
          Cancel
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      {showPreview && previewHTML && (
        <PreviewModal
          open={showPreview}
          onClose={() => handlePreviewClose()}
          html={previewHTML}
          invoiceData={previewData}
          mode="normal"
          showSnackbar={(snack) =>
            setSnackbar({
              open: true,
              message: snack.message,
              severity: snack.severity,
            })
          }
        />
      )}
    </Box>
  );
}
